import { ethers } from 'ethers';
import { useCallback, useEffect, useState } from 'react';
import getContract from '../../utils/getContract';
import { apiErrorMessage, fetchPerpetualRate } from '../../utils/api';
import { aprToRate, stakingAddress } from '../../config/config';
import { formatDate } from '../../utils/formatDate';
import stakingAbi from '../../ABI/stakingAbi.json';
import Loader from '../loader/Loader';
import Modal from '../modal/Modal';
import { useNotify } from '../notifications/NotificationsProvider';
import { useWallet } from '../wallet/WalletProvider';
import './RateDialog.css';

/**
 * Ставка бессрочного стейкинга и минимум взноса — окном, а не блоком на
 * странице.
 *
 * Меняют их редко, а место на экране блок занимал наравне с формой отправки:
 * крупное число, два поля ввода и таблица истории соседствовали с тем, чем
 * панель занята каждый день. Открывается по кнопке `RateButton` и живёт ровно
 * пока открыто: каждый показ читает условия заново, и устаревшего «было» в
 * окне подтверждения не бывает.
 *
 * Оба значения общие для всех позиций и меняются одной транзакцией от
 * `ADMIN_ROLE` — мгновенно, без потолка и таймлока. Поэтому окно подтверждения
 * здесь обязательно: ошибиться разрядом (120 вместо 12) стоит ровно один клик,
 * а откатить нельзя — накопленное по завышенной ставке уже зафиксировано.
 *
 * Текущие значения читаются у бэкенда, а не из контракта: панель не ходит в
 * блокчейн за чтением. Подпись — наоборот, только через кошелёк оператора.
 */

/** Что именно подтверждает оператор: две операции, два текста. */
const APR = 'apr';
const MIN_STAKE = 'minStake';

const shortHash = (hash) => `${hash.slice(0, 8)}…${hash.slice(-6)}`;

const RateDialog = ({ refreshKey, onClose, onRateChanged }) => {
  const { isReady, isKnownAdmin } = useWallet();
  const notify = useNotify();

  const [rate, setRate] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newApr, setNewApr] = useState('');
  const [newMinStake, setNewMinStake] = useState('');
  // Заявка на подпись, как в форме отправки: окно показывает готовые значения,
  // а отправка берёт их из заявки, а не перечитывает поля.
  const [request, setRequest] = useState(null);

  const load = useCallback(async () => {
    setLoadError('');

    try {
      setRate(await fetchPerpetualRate());
    } catch (error) {
      console.error('Не удалось загрузить ставку:', error);
      setRate(null);
      setLoadError(apiErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const disabled = loading || !isReady || Boolean(request);

  const handleAprClick = () => {
    const apr = Number(newApr.replace(',', '.'));

    if (!newApr || !Number.isFinite(apr) || apr < 0) {
      notify.error('Ставка должна быть числом не меньше нуля');
      return;
    }

    // Контракт хранит ставку в тысячных, то есть шаг — 0.1%. Значение с более
    // мелкой дробью молча округлилось бы при переводе, и оператор увидел бы в
    // реестре не то, что вводил.
    if (aprToRate(apr) / (1000 / 100) !== apr) {
      notify.error('Шаг ставки — 0,1%: контракт хранит её в тысячных');
      return;
    }

    setRequest({ kind: APR, apr, rateRaw: aprToRate(apr) });
  };

  const handleMinStakeClick = () => {
    const minStake = Number(newMinStake.replace(',', '.'));

    if (!newMinStake || !Number.isFinite(minStake) || minStake < 0) {
      notify.error('Минимум должен быть числом не меньше нуля');
      return;
    }

    setRequest({ kind: MIN_STAKE, minStake });
  };

  const send = async (confirmed) => {
    try {
      setLoading(true);
      const contract = await getContract(stakingAddress, stakingAbi);

      const tx =
        confirmed.kind === APR
          ? await contract.setPerpetualRate(confirmed.rateRaw)
          : await contract.setMinPerpetualStake(
              ethers.parseEther(String(confirmed.minStake)),
            );

      const receipt = await tx.wait();

      if (receipt.status === 1) {
        setNewApr('');
        setNewMinStake('');
        notify.success(
          confirmed.kind === APR
            ? `Ставка изменена на ${confirmed.apr}% годовых`
            : `Минимальный взнос изменён на ${confirmed.minStake} EMIVN`,
        );

        // Бэкенд читает ставку из контракта с кэшем в минуту, а историю — из
        // разобранных событий, которые приходят ещё позже. Поэтому список
        // ниже может секунду показывать прежнее значение; это норма.
        onRateChanged?.();
      }
    } catch (error) {
      console.error('TX error:', error);

      if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
        notify.info('Транзакция отклонена в кошельке');
        return;
      }

      notify.error(
        `Ошибка в транзакции: ${error.shortMessage ?? error.message ?? 'причина неизвестна'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    const confirmed = request;
    setRequest(null);
    void send(confirmed);
  };

  // Пока идёт транзакция, закрыть нельзя: закрытие размонтирует окно вместе с
  // состоянием отправки, и следующее открытие показало бы кнопки готовыми к
  // повторной подписи того же изменения.
  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  // Подтверждение показывается вместо окна условий, а не поверх него: два
  // модальных окна разом — это два захвата фокуса и два затемнения фона.
  // Сверять в этот момент всё равно нечего, кроме перехода «было → стало»,
  // а он в подтверждении и напечатан.
  if (request) {
    return (
      <Modal
        title="Подтвердите изменение"
        className="rate_confirm"
        onClose={() => setRequest(null)}
      >
        {request.kind === APR ? (
          <>
            <p className="rate_confirm__value">
              {rate?.apr ?? '—'}% → <b>{request.apr}%</b> годовых
            </p>
            <p className="rate_confirm__note">
              Новая ставка применится ко всем позициям с этого момента;
              накопленное ранее не изменится. Действует немедленно, откатить
              изменение нельзя — только выставить другую ставку.
            </p>
          </>
        ) : (
          <>
            <p className="rate_confirm__value">
              {rate?.minStake ?? '—'} → <b>{request.minStake} EMIVN</b>
            </p>
            <p className="rate_confirm__note">
              Минимум проверяется на каждом взносе, включая долив в существующую
              позицию. На уже внесённые тела не влияет.
            </p>
          </>
        )}

        <div className="modal_actions">
          <button type="button" onClick={() => setRequest(null)}>
            Отменить
          </button>
          <button type="button" autoFocus onClick={handleConfirm}>
            Подтвердить
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Условия бессрочного стейкинга"
      className="rate_dialog"
      onClose={handleClose}
    >
      {loadError && (
        <div className="rate_error">
          Не удалось загрузить ставку: {loadError}{' '}
          <button type="button" className="rate_error__retry" onClick={load}>
            Повторить
          </button>
        </div>
      )}

      <div className="rate_current">
        <div className="rate_current__value">
          {rate ? `${rate.apr}%` : '—'}
          <span className="rate_current__unit">годовых</span>
        </div>
        <div className="rate_current__min">
          Минимальный взнос: <b>{rate ? `${rate.minStake} EMIVN` : '—'}</b>
        </div>
      </div>

      {/* Нулевая ставка — рабочее состояние контракта, а не поломка, но
          означает она, что проценты не капают никому. Молчать об этом нельзя:
          взносы при нулевом APR принимаются, а тело не выводится. */}
      {rate && rate.aprRaw === 0 && (
        <div className="rate_warning">
          Ставка сейчас 0% — проценты не начисляются никому. Взносы при этом
          контракт принимает, а вывода тела в нём нет.
        </div>
      )}

      <div className="rate_forms">
        <div className="rate_form">
          <label htmlFor="rate_apr">Новая ставка, % годовых</label>
          <div className="rate_form__row">
            <input
              id="rate_apr"
              type="text"
              inputMode="decimal"
              placeholder="12"
              value={newApr}
              onChange={(event) => setNewApr(event.target.value)}
            />
            <button type="button" onClick={handleAprClick} disabled={disabled}>
              Изменить
            </button>
          </div>
        </div>

        <div className="rate_form">
          <label htmlFor="rate_min">Минимальный взнос, EMIVN</label>
          <div className="rate_form__row">
            <input
              id="rate_min"
              type="text"
              inputMode="decimal"
              placeholder="100"
              value={newMinStake}
              onChange={(event) => setNewMinStake(event.target.value)}
            />
            <button
              type="button"
              onClick={handleMinStakeClick}
              disabled={disabled}
            >
              Изменить
            </button>
          </div>
        </div>
      </div>

      {!isReady && (
        <div className="rate_notice">
          Подключите кошелёк в сети BSC, чтобы менять условия.
        </div>
      )}

      {/* Обе функции требуют ADMIN_ROLE — в отличие от бессрочного взноса.
          Предупредить дешевле, чем заплатить за ревёрт. */}
      {isReady && !isKnownAdmin && (
        <div className="rate_warning">
          Подключённый адрес не значится в списке админов — контракт, скорее
          всего, отклонит изменение. Обе операции требуют ADMIN_ROLE.
        </div>
      )}

      {loading && (
        <>
          <Loader />
          <div className="rate_notice">
            Идёт транзакция — окно не закроется, пока кошелёк не ответит.
          </div>
        </>
      )}

      <h5 className="rate_history__title">История изменений</h5>

      {rate && rate.history.length === 0 ? (
        <p className="rate_history__empty">
          Изменений не было. Сервис видит историю только с момента своего
          запуска: если ставку меняли до развёртывания бэкенда, той записи здесь
          не будет.
        </p>
      ) : (
        <div className="rate_history__scroll">
          <table className="rate_history">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Было</th>
                <th>Стало</th>
                <th>Изменил</th>
                <th>Транзакция</th>
              </tr>
            </thead>
            <tbody>
              {(rate?.history ?? []).map((change) => (
                <tr key={`${change.txHash}-${change.logIndex}`}>
                  <td>{formatDate(change.changedAt)}</td>
                  <td>{change.oldApr}%</td>
                  <td>
                    <b>{change.newApr}%</b>
                  </td>
                  <td>
                    <a
                      href={`https://bscscan.com/address/${change.sender}`}
                      target="_blank"
                      rel="noreferrer"
                      title={change.sender}
                    >
                      {change.sender.slice(0, 6)}…{change.sender.slice(-4)}
                    </a>
                  </td>
                  <td>
                    <a
                      href={`https://bscscan.com/tx/${change.txHash}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {shortHash(change.txHash)}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="modal_actions">
        <button type="button" onClick={handleClose} disabled={loading}>
          Закрыть
        </button>
      </div>
    </Modal>
  );
};

export default RateDialog;
